// The report list: only the reports the service returns (those the caller holds in full), each with the
// server's state labels and the actions the service allows.
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Button,
  Card,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import Loader from "components/Progress";
import { useGetReportsQuery } from "services/scheduledReports";
import { classifyReportError } from "utils/reportErrors";
import { formatWhen, periodText, scheduleText, scopeText } from "utils/reportDefinition";
import StatusChip from "../StatusChip";
import ReportActions from "../ReportActions";

const COLUMNS = ["Name", "Solutions", "Schedule", "State", "Next send", "Last result", ""];
const note = { fontSize: "12px", color: "grey", margin: "4px 0 0 0" };

function StateCell({ report }) {
  const { status = {}, solutionsGone = [], schedule } = report;
  return (
    <>
      <StatusChip state={status.state} label={status.label} />
      {status.since ? <p style={note}>Since {formatWhen(status.since, schedule && schedule.timeZone)}</p> : null}
      {solutionsGone.length ? (
        <p style={note}>{solutionsGone.length === 1 ? "1 solution no longer exists" : `${solutionsGone.length} solutions no longer exist`}</p>
      ) : null}
    </>
  );
}

function LastResultCell({ run }) {
  if (!run) return <span style={{ color: "grey" }}>Not sent yet</span>;
  return (
    <>
      <StatusChip state={run.state} label={run.label} />
      <p style={note}>{periodText(run)}</p>
      {run.reason ? <p style={note}>{run.reason}</p> : null}
    </>
  );
}

export default function List({ full }) {
  const metaDataValue = useSelector((state) => state.metaData);
  const [search, setSearch] = useState("");
  const { data, isLoading, error } = useGetReportsQuery(
    { token: window.localStorage.getItem("token") },
    { refetchOnMountOrArgChange: true }
  );
  const reports = (data && data.payload && data.payload.reports) || [];
  const needle = search.trim().toLowerCase();
  const shown = reports.filter((r) => String(r.name || "").toLowerCase().includes(needle));

  const content = () => {
    if (isLoading) return <Loader />;
    if (error && !data) return <p style={{ fontSize: "15px", color: "#c7c7c7", textAlign: "center" }}>{classifyReportError(error).message}</p>;
    if (!shown.length) {
      return (
        <p style={{ fontSize: "15px", color: "#c7c7c7", textAlign: "center" }}>
          {reports.length ? "No report matches this search." : "There are no scheduled reports here yet."}
        </p>
      );
    }
    return (
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              {COLUMNS.map((c) => (
                <TableCell key={c} style={{ fontWeight: 600, color: "#B3B4B6" }}>
                  {c}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {shown.map((r) => (
              <TableRow key={r.id} hover>
                <TableCell>
                  <Link to={`/scheduledReports/history/${r.id}`} style={{ color: metaDataValue.branding?.secondaryColor, fontWeight: 500 }}>
                    {r.name}
                  </Link>
                </TableCell>
                <TableCell>{scopeText(r.scope, metaDataValue.services)}</TableCell>
                <TableCell>{scheduleText(r.schedule)}</TableCell>
                <TableCell>
                  <StateCell report={r} />
                </TableCell>
                <TableCell>
                  {r.status && r.status.state === "paused" ? "Not while paused" : formatWhen(r.nextSendAt, r.schedule && r.schedule.timeZone)}
                </TableCell>
                <TableCell>
                  <LastResultCell run={r.lastRun} />
                </TableCell>
                <TableCell>
                  <ReportActions report={r} showView />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Card style={{ padding: "16px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "12px" }}>
        <p style={{ fontSize: "15px", fontWeight: "bold", color: "#333333", margin: 0, textTransform: "uppercase", flex: 1 }}>
          Scheduled reports
        </p>
        <TextField
          size="small"
          placeholder="Search by name"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        {full ? (
          <Button variant="contained" color="secondary" component={Link} to="/scheduledReports/new">
            New report
          </Button>
        ) : null}
      </div>
      {content()}
    </Card>
  );
}
